import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { adminApi, AgreementRecord } from '../services/api';

const AgreementsPage: React.FC = () => {
  const [agreements, setAgreements] = useState<AgreementRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Generation state
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [customerName, setCustomerName] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().split('T')[0]);
  const [monthlyFee, setMonthlyFee] = useState('');
  const [numLicenses, setNumLicenses] = useState(1);
  const [numRuns, setNumRuns] = useState(50);
  const [duration, setDuration] = useState(12);

  const fetchAgreements = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.listAgreements();
      if (response.success) {
        setAgreements(response.agreements || []);
      } else {
        setError(response.error?.message || 'Failed to fetch Agreements');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || err.message || 'An unexpected error occurred';
      console.error('Error fetching Agreements:', err);
      setError(errorMessage);
      setAgreements([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgreements();
  }, [fetchAgreements]);

  const handleGenerate = async () => {
    if (!customerName || !customerAddress || !effectiveDate || !monthlyFee) {
      setGenError('Customer Name, Address, Effective Date, and Monthly Fee are required');
      return;
    }

    if (numLicenses < 1 || numRuns < 1 || duration < 1) {
      setGenError('Licenses, Runs, and Duration must be positive numbers');
      return;
    }

    setGenerating(true);
    setGenError(null);
    try {
      const response = await adminApi.generateAgreement({
        customerName,
        customerAddress,
        effectiveDate,
        monthlyFee,
        numLicenses,
        numRuns,
        duration,
      });

      if (response.success) {
        setGenerateOpen(false);
        setCustomerName('');
        setCustomerAddress('');
        setEffectiveDate(new Date().toISOString().split('T')[0]);
        setMonthlyFee('');
        setNumLicenses(1);
        setNumRuns(50);
        setDuration(12);
        
        // Trigger automatic download
        if (response.downloadUrl) {
          window.open(response.downloadUrl, '_blank');
        }
        
        // Refresh the list
        fetchAgreements();
      } else {
        setGenError(response.error?.message || 'Failed to generate Agreement');
      }
    } catch (err: any) {
      setGenError(err.response?.data?.error?.message || err.message || 'An unexpected error occurred');
    } finally {
      setGenerating(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          Agreement Management
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchAgreements}
            disabled={loading}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setGenerateOpen(true)}
          >
            Generate Agreement
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Customer Name</TableCell>
                  <TableCell>Generated At</TableCell>
                  <TableCell>File Size</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : agreements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      No Agreements found
                    </TableCell>
                  </TableRow>
                ) : (
                  agreements.map((agreement) => (
                    <TableRow key={agreement.filename}>
                      <TableCell sx={{ fontWeight: 500 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <DescriptionIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                          {agreement.customerName}
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(agreement.generatedAt)}</TableCell>
                      <TableCell>{formatFileSize(agreement.fileSize)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Download Agreement">
                          <IconButton 
                            onClick={() => window.open(agreement.url, '_blank')}
                            color="primary"
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Generate Agreement Dialog */}
      <Dialog open={generateOpen} onClose={() => !generating && setGenerateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate New Agreement</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {genError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {genError}
              </Alert>
            )}
            <TextField
              fullWidth
              label="Customer Name"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              margin="normal"
              disabled={generating}
              placeholder="e.g. ACME Corporation"
              required
            />
            <TextField
              fullWidth
              label="Customer Address"
              value={customerAddress}
              onChange={(e) => setCustomerAddress(e.target.value)}
              margin="normal"
              multiline
              rows={3}
              disabled={generating}
              placeholder="Full physical address"
              required
            />
            <TextField
              fullWidth
              label="Effective Date"
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              margin="normal"
              disabled={generating}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              fullWidth
              label="Monthly Fee"
              value={monthlyFee}
              onChange={(e) => setMonthlyFee(e.target.value)}
              margin="normal"
              disabled={generating}
              placeholder="R 5,000.00"
              required
            />
            <TextField
              fullWidth
              label="Number of Licenses"
              type="number"
              value={numLicenses}
              onChange={(e) => setNumLicenses(parseInt(e.target.value) || 1)}
              margin="normal"
              disabled={generating}
              InputProps={{ inputProps: { min: 1 } }}
              required
            />
            <TextField
              fullWidth
              label="Number of Runs per Month"
              type="number"
              value={numRuns}
              onChange={(e) => setNumRuns(parseInt(e.target.value) || 1)}
              margin="normal"
              disabled={generating}
              InputProps={{ inputProps: { min: 1 } }}
              required
            />
            <TextField
              fullWidth
              label="Duration (months)"
              type="number"
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value) || 1)}
              margin="normal"
              disabled={generating}
              InputProps={{ inputProps: { min: 1 } }}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateOpen(false)} disabled={generating}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            variant="contained"
            disabled={generating}
            startIcon={generating && <CircularProgress size={20} color="inherit" />}
          >
            {generating ? 'Generating...' : 'Generate & Download'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgreementsPage;
